import { useState, useEffect } from "react";
import { Plus, BookOpen, BarChart2, Bookmark, Trash2, Edit2, X, Save } from "lucide-react";
import "./Biblioteca.css";

export default function Biblioteca() {
    const [books, setBooks] = useState(() => {
        const saved = localStorage.getItem("myWorkspaceBooks");
        return saved ? JSON.parse(saved) : [];
    });

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [form, setForm] = useState({
        title: "", author: "", genre: "", pagesTotal: "", pagesRead: "",
        status: "Lendo", cover: ""
    });

    useEffect(() => {
        localStorage.setItem("myWorkspaceBooks", JSON.stringify(books));
    }, [books]);

    // --- ESTATÍSTICAS ---
    const totalRead = books.filter(b => b.status === "Lido").length;
    const totalWantToRead = books.filter(b => b.status === "Quero Ler").length;

    const currentReading = books.find(b => b.status === "Lendo");

    const currentProgress = currentReading && Number(currentReading.pagesTotal) > 0
        ? Math.min(100, Math.round((currentReading.pagesRead / currentReading.pagesTotal) * 100)) || 0
        : 0;

    // --- HANDLERS ---
    const handleSave = (e) => {
        e.preventDefault();
        if (!form.title || !form.pagesTotal) return;

        const newBook = { ...form, id: editingId || Date.now() };

        if (editingId) {
            setBooks(books.map(b => b.id === editingId ? newBook : b));
        } else {
            setBooks([...books, newBook]);
        }
        resetForm();
    };

    const handleEdit = (book) => {
        setForm({ ...book });
        setEditingId(book.id);
        setShowForm(true);
    };

    const handleDelete = (id) => {
        if (window.confirm("Remover este livro da biblioteca?")) {
            setBooks(books.filter(b => b.id !== id));
        }
    };

    const resetForm = () => {
        setForm({ title: "", author: "", genre: "", pagesTotal: "", pagesRead: "", status: "Lendo", cover: "" });
        setEditingId(null);
        setShowForm(false);
    };

    return (
        <div className="biblioteca-container">
            <header className="biblioteca-header">
                <div>
                    <h1>Biblioteca</h1>
                    <p>Gerencie seu acervo, leituras e progresso.</p>
                </div>
                <button className="btn-add-book" onClick={() => setShowForm(true)}>
                    <Plus size={20} /> Adicionar Livro
                </button>
            </header>

            {/* --- DASHBOARD --- */}
            <div className="books-stats-grid">
                <div className="stat-card">
                    <div className="icon-box blue"><BookOpen size={24} /></div>
                    <div>
                        <h3>Lidos (Ano)</h3>
                        <span className="stat-number">{totalRead}</span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="icon-box green"><BarChart2 size={24} /></div>
                    <div>
                        <h3>Lendo Agora</h3>
                        <span className="stat-text-highlight">
                            {currentReading ? currentReading.title : "Nenhum"}
                        </span>
                        {currentReading && (
                            <div className="mini-progress-bar">
                                <div className="fill" style={{ width: `${currentProgress}%` }}></div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="stat-card">
                    <div className="icon-box purple"><Bookmark size={24} /></div>
                    <div>
                        <h3>Na Lista</h3>
                        <span className="stat-number">{totalWantToRead}</span>
                    </div>
                </div>
            </div>

            {/* --- FORMULÁRIO --- */}
            {showForm && (
                <div className="book-form-overlay">
                    <form className="book-form" onSubmit={handleSave}>
                        <div className="book-form-header">
                            <h2>{editingId ? "Editar Livro" : "Novo na Estante"}</h2>
                            <button type="button" onClick={resetForm}><X size={24} /></button>
                        </div>

                        <div className="book-form-grid">
                            <input
                                placeholder="Título do Livro"
                                value={form.title}
                                onChange={e => setForm({ ...form, title: e.target.value })}
                                required
                            />
                            <input
                                placeholder="Autor"
                                value={form.author}
                                onChange={e => setForm({ ...form, author: e.target.value })}
                            />
                            <div className="book-form-row-2">
                                <input
                                    placeholder="Gênero"
                                    value={form.genre}
                                    onChange={e => setForm({ ...form, genre: e.target.value })}
                                />
                                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                    <option value="Quero Ler">Quero Ler</option>
                                    <option value="Lendo">Lendo</option>
                                    <option value="Lido">Lido</option>
                                    <option value="Interrompido">Interrompido</option>
                                </select>
                            </div>
                            <div className="book-form-row-2">
                                <input
                                    type="number"
                                    placeholder="Páginas Totais"
                                    value={form.pagesTotal}
                                    onChange={e => setForm({ ...form, pagesTotal: e.target.value })}
                                    required
                                />
                                <input
                                    type="number"
                                    placeholder="Páginas Lidas"
                                    value={form.pagesRead}
                                    onChange={e => setForm({ ...form, pagesRead: e.target.value })}
                                />
                            </div>

                            <input
                                placeholder="URL da Capa (Imagem)"
                                value={form.cover}
                                onChange={e => setForm({ ...form, cover: e.target.value })}
                            />
                        </div>
                        <button type="submit" className="btn-save-form"><Save size={18} /> Salvar</button>
                    </form>
                </div>
            )}

            {/* --- ESTANTE (GRID) --- */}
            <div className="library-grid">
                {books.length === 0 && (
                    <p style={{ color: 'var(--text-muted)', gridColumn: '1/-1', textAlign: 'center' }}>
                        Sua biblioteca está vazia.
                    </p>
                )}

                {books.map(book => {
                    const percent = Number(book.pagesTotal) > 0
                        ? Math.min(100, Math.round((book.pagesRead / book.pagesTotal) * 100)) || 0
                        : 0;

                    return (
                        <div key={book.id} className="book-card">
                            <div
                                className="book-cover"
                                style={{
                                    backgroundImage: `url("${book.cover || 'https://via.placeholder.com/150x220?text=Capa'}")`
                                }}
                            >
                                <span className={`status-badge ${(book.status || "").replace(" ", "").toLowerCase()}`}>
                                    {book.status}
                                </span>
                            </div>

                            <div className="book-info">
                                <h3>{book.title}</h3>
                                <p className="author">{book.author}</p>

                                <div className="progress-section">
                                    <div className="progress-text">
                                        <span>{percent}%</span>
                                    </div>
                                    <div className="progress-bar-bg">
                                        <div className="progress-bar-fill" style={{ width: `${percent}%` }}></div>
                                    </div>
                                </div>

                                <div className="book-actions">
                                    <button onClick={() => handleEdit(book)}><Edit2 size={16} /></button>
                                    <button onClick={() => handleDelete(book.id)} className="del"><Trash2 size={16} /></button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}